import { useEffect, useId, useMemo, useRef, useState } from 'react';
import { Link } from 'react-router';
import { api } from '../api/client.ts';
import type { VersionSummary } from '../api/types.ts';
import { t } from '../i18n/index.ts';
import { assistantPrompts, type AskView } from '../lib/assistantPrompts.ts';
import { now } from '../lib/clock.ts';
import { agoLabel } from '../lib/format.ts';
import { useLocalPreference } from '../lib/hooks.ts';
import { usePlayground } from '../state/playground.ts';
import { useActivePlan, useStore } from '../state/store.tsx';
import { Icon } from './Icon.tsx';
import { Modal, SectionHeader, sourceName } from './ui.tsx';

type Connected = 'unknown' | 'yes' | 'no';

let lastKnown: Connected = 'unknown';

/** Whether any assistant is connected over MCP. A failed check is 'unknown', so nobody is nagged to connect because of an outage. */
export function useAssistantConnected(): Connected {
  const playground = usePlayground();
  const [state, setState] = useState<Connected>(playground ? 'no' : lastKnown);
  useEffect(() => {
    if (playground) return;
    let live = true;
    api.mcpConnections().then(
      ({ connections }) => {
        lastKnown = connections.length > 0 ? 'yes' : 'no';
        if (live) setState(lastKnown);
      },
      (error: unknown) => {
        lastKnown = (error as { status?: number }).status === 404 ? 'no' : 'unknown';
        if (live) setState(lastKnown);
      },
    );
    return () => {
      live = false;
    };
  }, [playground]);
  return state;
}

function selectText(element: HTMLElement | null) {
  const selection = window.getSelection();
  if (!element || !selection) return;
  const range = document.createRange();
  range.selectNodeContents(element);
  selection.removeAllRanges();
  selection.addRange(range);
}

function PromptRow({ prompt }: { prompt: string }) {
  const [state, setState] = useState<'idle' | 'copied' | 'selected'>('idle');
  const text = useRef<HTMLSpanElement>(null);
  const textId = useId();
  const timer = useRef<number | undefined>(undefined);
  useEffect(() => () => window.clearTimeout(timer.current), []);
  const copy = () => {
    const done = (next: 'copied' | 'selected') => {
      setState(next);
      window.clearTimeout(timer.current);
      timer.current = window.setTimeout(() => setState('idle'), 2400);
    };
    const fallback = () => {
      selectText(text.current);
      done('selected');
    };
    try {
      if (!navigator.clipboard) throw new Error('no clipboard');
      navigator.clipboard.writeText(prompt).then(() => done('copied'), fallback);
    } catch {
      fallback();
    }
  };
  return (
    <li className="ask__prompt">
      <span className="ask__text" id={textId} ref={text}>{prompt}</span>
      <button type="button" className={`ask__copy ${state === 'copied' ? 'ask__copy--done' : ''}`} onClick={copy} aria-label={t('ask.copyLabel')} aria-describedby={textId}>
        <Icon name={state === 'copied' ? 'check' : 'copy'} size={14} strokeWidth={2.2} />
        <span aria-hidden="true">{state === 'copied' ? t('ask.copied') : t('common.copy')}</span>
      </button>
      <span className="sr-only" role="status">{state === 'copied' ? t('ask.copied') : ''}</span>
      {state === 'selected' && <span className="ask__hint" role="status">{t('ask.selected')}</span>}
    </li>
  );
}

/** Example questions for the user's assistant, written for this screen from the plan's own items. */
export function AskAssistant({ view, className = '' }: { view: AskView; className?: string }) {
  const { plan } = useStore();
  const [collapsed, setCollapsed] = useLocalPreference('mm.ask.collapsed', false);
  const connected = useAssistantConnected();
  const titleId = useId();
  const bodyId = useId();
  const prompts = useMemo(() => (plan ? assistantPrompts(plan, view) : []), [plan, view]);
  if (prompts.length === 0) return null;
  return (
    <section className={`card ask ${collapsed ? 'ask--collapsed' : ''} ${className}`} aria-labelledby={titleId} data-testid="ask-assistant" data-connected={connected}>
      <h2 className="ask__heading">
        <button
          type="button"
          className="ask__toggle"
          aria-expanded={!collapsed}
          aria-controls={bodyId}
          onClick={() => setCollapsed(!collapsed)}
        >
          <span className="ask__mark" aria-hidden="true"><Icon name="chat" size={14} /></span>
          <span id={titleId} className="ask__title">{t('ask.title')}</span>
          <span className="sr-only">{collapsed ? t('ask.expand') : t('ask.collapse')}</span>
          <Icon name="chevronDown" size={16} className="ask__chevron" />
        </button>
      </h2>
      <div id={bodyId} className="ask__body" hidden={collapsed}>
        <ul className="ask__prompts">
          {prompts.map((p) => <PromptRow key={p} prompt={p} />)}
        </ul>
        {connected === 'no' ? (
          <div className="ask__connect">
            <p className="ask__footer">{t('ask.connectHint')}</p>
            <Link to="/connections" className="button button--sm button--primary">{t('ask.connect')}</Link>
          </div>
        ) : (
          <p className="ask__footer">
            {t('ask.footer')} <Link to="/connections" className="text-link">{t('ask.footerLink')}</Link>
          </p>
        )}
      </div>
    </section>
  );
}

const WEEK_MS = 7 * 86_400_000;

/** Changes an assistant made in the last week, newest first, with a one-tap undo of the latest when it is still current. */
export function AssistantActivity({ className = '' }: { className?: string }) {
  const { versions, restore, saving } = useStore();
  const { canEdit } = useActivePlan();
  const [confirming, setConfirming] = useState<VersionSummary | null>(null);
  const id = useId();
  const clock = now();
  const rows = versions
    .filter((v) => v.source.startsWith('mcp:') && clock.getTime() - new Date(v.createdAt).getTime() <= WEEK_MS)
    .slice(0, 5);
  if (rows.length === 0) return null;
  const head = versions[0];
  const undo = async (v: VersionSummary) => {
    setConfirming(null);
    await restore(v.version - 1, 'activity.stale');
  };
  return (
    <section className={`card card--pad-lg activity ${className}`} aria-labelledby={id} data-testid="assistant-activity">
      <SectionHeader title={t('activity.title')} id={id} action={<Link to="/connections#history" className="text-link">{t('common.history')}</Link>} />
      <ul className="activity__list">
        {rows.map((v, i) => {
          const assistant = sourceName(v.source);
          const undoable = i === 0 && v === head && v.version > 1 && canEdit;
          return (
            <li key={v.version} className="activity__row">
              <span className={`activity__who source-badge source-badge--${v.source.slice(4)}`}>{assistant}</span>
              <span className="activity__summary" title={v.summary}>{v.summary}</span>
              <span className="activity__meta">
                <span className="activity__when">{agoLabel(v.createdAt, clock)}</span>
                <Link to={`/connections?since=${v.version - 1}#history`} className="text-link" aria-label={t('activity.viewLabel', { summary: v.summary })}>
                  {t('activity.view')}
                </Link>
                {undoable && (
                  <button type="button" className="link-button activity__undo" disabled={saving} onClick={() => setConfirming(v)} aria-label={t('activity.undoLabel', { summary: v.summary })}>
                    {t('common.undo')}
                  </button>
                )}
              </span>
            </li>
          );
        })}
      </ul>
      {confirming && <UndoDialog version={confirming} onClose={() => setConfirming(null)} onConfirm={() => void undo(confirming)} />}
    </section>
  );
}

function UndoDialog({ version, onClose, onConfirm }: { version: VersionSummary; onClose: () => void; onConfirm: () => void }) {
  const bodyId = useId();
  return (
    <Modal
      title={t('activity.undoTitle')}
      onClose={onClose}
      describedBy={bodyId}
      footer={
        <>
          <button type="button" className="button button--ghost" onClick={onClose}>{t('common.cancel')}</button>
          <button type="button" className="button button--amber" onClick={onConfirm}>{t('activity.undoConfirm')}</button>
        </>
      }
    >
      <div className="undo-dialog">
        <span className="undo-dialog__icon" aria-hidden="true"><Icon name="alert" size={18} /></span>
        <p id={bodyId} className="modal__text">
          {t('activity.undoBody', { assistant: sourceName(version.source), summary: version.summary, version: version.version - 1 })}
        </p>
      </div>
    </Modal>
  );
}
